import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, CardHeader, Container } from "reactstrap";
import { Col, Row } from "reactstrap";
import Breadcrumbs from "@components/Common/Breadcrumb";
import TableContainer from "@components/Common/TableContainer";
import api from "@/shared/services/api";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  formatDate,
  formatDateTime,
  formatTimestampDate,
} from "@/helpers/date_helper";
import { ColumnDef } from "@tanstack/react-table";

const paymentPlanStatusLabels: Record<string, string> = {
  void: "Pendiente",
  "pending-approval": "Pendiente de aprobación",
  pending: "Pendiente",
  paid: "Pagado",
  overdue: "Vencido",
  inArrears: "En mora",
  rejected: "Rechazado",
};

const amountFormatter = new Intl.NumberFormat("es-VE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const rateFormatter = new Intl.NumberFormat("es-VE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

type ProfitabilityRow = {
  label: string;
  key: string;
  expense?: boolean;
  emphasis?: boolean;
};

type DetailNavigationState = {
  backTitle?: string;
  backUrl?: string;
};

const getProfitabilityRows = (
  totalLabel: string,
  bankCommissionRows: ProfitabilityRow[],
  profitLabel: string,
): ProfitabilityRow[] => [
  { label: totalLabel, key: "totalDueUsd", emphasis: true },
  { label: "Capital", key: "principalUsd" },
  { label: "Interés", key: "interestUsd" },
  { label: "Comisión", key: "commissionUsd" },
  { label: "IVA (Sobre la comisión)", key: "ivaUsd", expense: true },
  ...bankCommissionRows.map((row) => ({ ...row, expense: true })),
  { label: profitLabel, key: "profitUsd", emphasis: true },
];

const toFiniteNumber = (value: unknown) => {
  const numberValue = Number(value ?? 0);

  return Number.isFinite(numberValue) ? numberValue : 0;
};

const isManualReviewLaStatus = (laStatus?: string) =>
  String(laStatus || "").startsWith("manual_review_");

const formatUsd = (value: unknown) =>
  `$ ${amountFormatter.format(toFiniteNumber(value))}`;
const formatVef = (value: unknown) =>
  `Bs. ${amountFormatter.format(toFiniteNumber(value))}`;
const formatRate = (value: unknown) =>
  `Bs. ${rateFormatter.format(toFiniteNumber(value))}`;

function OperationDetails({
  endpointBase = "/operation/get",
  breadcrumbTitle = "Operaciones",
  backUrl = "/operations/list",
}: {
  endpointBase?: string;
  breadcrumbTitle?: string;
  backUrl?: string;
}) {
  const title = "Detalle de la operación";
  document.title = `${title} | ${import.meta.env.VITE_APP_TITLE}`;

  const { id } = useParams();
  const location = useLocation();
  const navigationState = location.state as DetailNavigationState | null;
  const breadcrumbTitleFromState = navigationState?.backTitle || breadcrumbTitle;
  const breadcrumbBackUrl = navigationState?.backUrl || backUrl;

  const [data, setData] = useState<any>(undefined);
  const [loading, setLoading] = useState(false);
  const isManualReview = isManualReviewLaStatus(data?.laStatus);

  const getUsdAmount = (vefAmount: unknown, usdAmount?: unknown) => {
    if (usdAmount !== undefined && usdAmount !== null) {
      return toFiniteNumber(usdAmount);
    }

    const rate = toFiniteNumber(data?.rate);

    return rate > 0 ? toFiniteNumber(vefAmount) / rate : 0;
  };

  const renderDualAmount = (vefAmount: unknown, usdAmount?: unknown) => (
    <div>
      <div>{formatUsd(getUsdAmount(vefAmount, usdAmount))}</div>
      <small className="text-muted">{formatVef(vefAmount)}</small>
    </div>
  );

  const renderProfitabilityCard = (
    title: string,
    totals: any,
    totalLabel = "Total a cobrar",
    bankCommissionRows: ProfitabilityRow[] = [
      { label: "Comisión Bancaria", key: "bankCommissionUsd" },
    ],
    profitLabel = "Ganancia Esperada",
  ) => (
    <Card className="h-100 mb-0">
      <CardHeader>
        <h3 className="card-title mb-0">{title}</h3>
      </CardHeader>
      <CardBody>
        {getProfitabilityRows(
          totalLabel,
          bankCommissionRows,
          profitLabel,
        ).map((row) => {
          const amountClassName = [
            row.emphasis ? "fs-5" : "",
            row.expense ? "text-danger" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <div
              className="d-flex align-items-center justify-content-between gap-3 mb-3"
              key={row.key}
            >
              <span className="text-muted">{row.label}</span>
              <strong className={amountClassName}>
                {formatUsd(totals?.[row.key])}
              </strong>
            </div>
          );
        })}
      </CardBody>
    </Card>
  );

  const getData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`${endpointBase}/${id}`);
      setData(response.data.data);
    } catch (error: any) {
      console.error("Error fetching operation:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData(); // Llama a la función para obtener los clientes
  }, [endpointBase, id]);

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        header: "Fecha de pago",
        accessorKey: "paidAt",
        cell: (cell: any) =>
          cell?.getValue() ? formatTimestampDate(cell.getValue()) : "-",
      },
      {
        header: "Fecha de cobro",
        accessorKey: "date",
        cell: (cell: any) => formatDate(cell.getValue()),
      },
      {
        header: "Capital",
        accessorKey: "principalUsd",
        cell: (cell: any) => (
          <span className="fw-medium">{formatUsd(cell.getValue())}</span>
        ),
      },
      {
        header: "Interés",
        accessorKey: "interestUsd",
        cell: (cell: any) => (
          <span className="fw-medium">{formatUsd(cell.getValue())}</span>
        ),
      },
      {
        header: "Comisión",
        accessorKey: "commissionUsd",
        cell: (cell: any) => (
          <span className="fw-medium">{formatUsd(cell.getValue())}</span>
        ),
      },
      {
        header: "IVA",
        accessorKey: "ivaUsd",
        cell: (cell: any) => (
          <span className="fw-medium">{formatUsd(cell.getValue())}</span>
        ),
      },
      {
        header: "Total",
        accessorKey: "totalDueUsd",
        cell: (cell: any) => (
          <h5 className="font-size-14 mb-1">{formatUsd(cell.getValue())}</h5>
        ),
      },
      {
        header: "Estatus",
        accessorKey: "status",
        cell: (cell: any) => {
          const row = cell.row.original;
          const statusId = row.status;
          const statusLabel =
            paymentPlanStatusLabels[statusId] ||
            row.statusName ||
            "Desconocido";
          let statusColor = "bg-info";
          if (statusId === "paid") {
            statusColor = "bg-success";
          } else if (statusId === "overdue") {
            statusColor = "bg-warning text-dark";
          } else if (statusId === "inArrears") {
            statusColor = "bg-danger";
          } else if (statusId === "rejected") {
            statusColor = "bg-danger";
          }
          return (
            <span className={`rounded-pill ${statusColor} badge`}>
              {statusLabel}
            </span>
          );
        },
      },
    ],
    [],
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={breadcrumbTitleFromState}
            breadcrumbItem={title}
            url={breadcrumbBackUrl}
          />
          <Row>
            <Col xl={12}>
              {data ? (
                <>
                  <Row className="g-3 mb-4">
                    <Col xl={12}>
                      <Card className="h-100 mb-0">
                        <CardHeader>
                          <h3 className="card-title mb-0">
                            Datos del préstamo
                          </h3>
                        </CardHeader>
                        <CardBody>
                          <table className="table table-sm table-borderless mb-0">
                            <tbody>
                              <tr className="fs-5">
                                <th className="pe-3">Monto:</th>
                                <td>
                                  {renderDualAmount(
                                    data.amountVef,
                                    data.amountUsd,
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <th className="pe-3">Fecha:</th>
                                <td>{formatDateTime(data.createdAt)}</td>
                              </tr>
                              <tr>
                                <th className="pe-3">Nombre cliente:</th>
                                <td>
                                  <Link
                                    to={`/clients/edit/${data.clientId}`}
                                    className="text-dark fw-bold"
                                  >
                                    {data.clientName}
                                  </Link>
                                </td>
                              </tr>
                              <tr>
                                <th className="pe-3">Correo cliente:</th>
                                <td>{data.clientEmail}</td>
                              </tr>
                              <tr>
                                <th className="pe-3">Tasa BCV:</th>
                                <td>{formatRate(data.rate)}</td>
                              </tr>
                              <tr>
                                <th className="pe-3">Cuotas:</th>
                                <td>{data.feeCount}</td>
                              </tr>
                              <tr>
                                <th className="pe-3">
                                  Monto comisión bancaria:
                                </th>
                                <td>
                                  {renderDualAmount(data.commissionAmount)}
                                </td>
                              </tr>
                              <tr>
                                <th className="pe-3">Monto liquidado:</th>
                                <td>{renderDualAmount(data.settledAmount)}</td>
                              </tr>
                              <tr>
                                <th className="pe-3">Estado:</th>
                                <td>{isManualReview ? "Revisión manual" : data.statusName}</td>
                              </tr>
                              {isManualReview && (
                                <tr>
                                  <th className="pe-3">Mensaje de revisión:</th>
                                  <td>{data.syncError || "-"}</td>
                                </tr>
                              )}
                              {data.status === "rejected" && (
                                <>
                                  <tr>
                                    <th className="pe-3">
                                      Borrado de LA Sistemas:
                                    </th>
                                    <td>
                                      {data.laStatus === "voided" ? "Sí" : "No"}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th className="pe-3">Razón de rechazo:</th>
                                    <td>{data.syncError || "-"}</td>
                                  </tr>
                                </>
                              )}
                              <tr>
                                <th className="pe-3">Referencia:</th>
                                <td>{data.reference}</td>
                              </tr>
                              <tr>
                                <th className="pe-3">Bank Tx ID:</th>
                                <td>{data.bankTxId || "-"}</td>
                              </tr>
                              <tr>
                                <th className="pe-3">Comentario:</th>
                                <td>{data.comment}</td>
                              </tr>
                            </tbody>
                          </table>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col xl={6} md={6}>
                      {renderProfitabilityCard(
                        "Rentabilidad Esperada",
                        data.profitability?.expected,
                        "Total a cobrar",
                        [
                          {
                            label: "Comisión Bancaria (Débito directo)",
                            key: "bankCommissionDebitUsd",
                          },
                        ],
                        "Ganancia Esperada",
                      )}
                    </Col>
                    <Col xl={6} md={6}>
                      {renderProfitabilityCard(
                        "Rentabilidad Obtenida",
                        data.profitability?.obtained,
                        "Total cobrado",
                        [
                          {
                            label: "Comisión Bancaria (Débito directo)",
                            key: "bankCommissionDebitUsd",
                          },
                          {
                            label: "Comisión Bancaria (Pago móvil)",
                            key: "bankCommissionMobileUsd",
                          },
                        ],
                        "Ganancia Obtenida",
                      )}
                    </Col>
                  </Row>
                  <Card>
                    <CardHeader>
                      <h3 className="card-title">Plan de pago</h3>
                    </CardHeader>
                    <CardBody>
                      <TableContainer
                        columns={columns}
                        data={data.paymentPlan} // Pasa los datos obtenidos de la API
                        hidePagination={true}
                        isAddUserList={true}
                        customPageSize={50}
                        className="table align-middle datatable dt-responsive table-check nowrap"
                      />
                    </CardBody>
                  </Card>
                </>
              ) : null}
            </Col>
            {/* <Col lg="12">
                <Row className="align-items-center">
                  <Col md={6}>
                    <div className="mb-3">
                      <h5 className="card-title">
                        Total{" "}
                        <span className="text-muted fw-normal ms-2">
                          ({data.length})
                        </span>
                      </h5>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col xl="12">
                    <TableContainerServer
                      columns={columns}
                      data={data}
                      isGlobalFilter={true}
                      isAddUserList={true}
                      pagination={pagination}
                      setPagination={setPagination}
                      pageCount={pageCount}
                      totalCount={totalCount}
                      globalFilter={globalFilter} // Pass globalFilter state
                      setGlobalFilter={setGlobalFilter} // Pass setGlobalFilter function
                      setSorting={[] as any} // Pass setSorting
                      setColumnFilters={[] as any} // Pass setColumnFilters
                      className="table align-middle datatable dt-responsive table-check nowrap"
                    />
                  </Col>
                </Row>
              </Col> */}
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default OperationDetails;
